(() => {
  'use strict';

  const SECTION_SELECTOR = '.nextdev--header--section';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const instances = new Map();

  const init = (SECTION) => {
    if (instances.has(SECTION)) return;

    const CART_ICON = SECTION.querySelector('.nextdev--header--icons__cart');
    const MENU = SECTION.querySelector('.nextdev--mobile-menu');
    const PANEL = MENU?.querySelector('.nextdev--mobile-menu--container');
    const OPEN_BTN = SECTION.querySelector('.nextdev--header--menu__mobile button');
    const CLOSE_BTN = MENU?.querySelector('.nextdev--mobile-menu--header button');

    const controller = new AbortController();
    const { signal } = controller;

    instances.set(SECTION, { controller, menu: null });

    /* Cart drawer */
    if (CART_ICON) {
      CART_ICON.addEventListener(
        'click',
        (e) => {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') {
            e.preventDefault();
            drawer.open();
          }
        },
        { signal }
      );
    }

    if (!MENU || !PANEL || !OPEN_BTN || !CLOSE_BTN) return;

    /* Submenus */
    SECTION.querySelectorAll('.nextdev--mobile-menu--links__list > li').forEach((item) => {
      const link = item.querySelector('a');
      const sublist = item.querySelector('.nextdev--mobile-menu--links__list--sublist');
      if (!sublist || !link) return;

      const isOpen = item.classList.contains('open');
      link.setAttribute('aria-expanded', String(isOpen));
      sublist.inert = !isOpen;

      link.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          const willOpen = !item.classList.contains('open');

          if (willOpen) {
            sublist.style.setProperty('--height', sublist.scrollHeight + 'px');
          }

          item.classList.toggle('open', willOpen);
          link.setAttribute('aria-expanded', String(willOpen));
          sublist.inert = !willOpen;
        },
        { signal }
      );

      sublist.addEventListener(
        'transitionend',
        (e) => {
          if (e.propertyName !== 'height') return;
          if (item.classList.contains('open')) {
            sublist.style.setProperty('--height', 'auto');
          }
        },
        { signal }
      );
    });

    /* Menu open / close */
    let lastFocused = null;

    const isOpen = () => MENU.classList.contains('show');

    const openMenu = () => {
      if (isOpen()) return;
      lastFocused = document.activeElement;
      MENU.inert = false;
      MENU.classList.add('show');
      OPEN_BTN.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      CLOSE_BTN.focus();
    };

    const closeMenu = ({ restoreFocus = true } = {}) => {
      if (!isOpen()) return;
      MENU.classList.remove('show');
      OPEN_BTN.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      MENU.inert = true;
      if (restoreFocus && lastFocused && document.contains(lastFocused)) {
        lastFocused.focus();
      }
    };

    const trapFocus = (e) => {
      if (e.key !== 'Tab' || !isOpen()) return;

      const focusables = Array.from(PANEL.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null && !el.closest('[inert]')
      );
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || !PANEL.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    MENU.inert = true;

    OPEN_BTN.addEventListener('click', openMenu, { signal });
    CLOSE_BTN.addEventListener('click', () => closeMenu(), { signal });

    MENU.addEventListener(
      'click',
      (e) => {
        if (!PANEL.contains(e.target)) closeMenu();
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') closeMenu();
        else trapFocus(e);
      },
      { signal }
    );

    window
      .matchMedia('(min-width: 750px)')
      .addEventListener('change', (e) => {
        if (e.matches) closeMenu({ restoreFocus: false });
      }, { signal });

    instances.set(SECTION, { controller, menu: { openMenu, closeMenu } });
  };

  const destroy = (SECTION) => {
    const instance = instances.get(SECTION);
    if (!instance) return;
    instance.controller.abort();
    document.body.classList.remove('overflow-hidden');
    instances.delete(SECTION);
  };

  const resolve = (target) =>
    target?.matches?.(SECTION_SELECTOR) ? target : target?.querySelector?.(SECTION_SELECTOR);

  document.querySelectorAll(SECTION_SELECTOR).forEach(init);

  document.addEventListener('shopify:section:load', (e) => {
    const section = resolve(e.target);
    if (section) init(section);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = resolve(e.target);
    if (section) destroy(section);
  });

  document.addEventListener('shopify:block:select', (e) => {
    const section = e.target.closest(SECTION_SELECTOR);
    instances.get(section)?.menu?.openMenu();
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    const section = e.target.closest(SECTION_SELECTOR);
    instances.get(section)?.menu?.closeMenu({ restoreFocus: false });
  });
})();